<template>
  <!-- Step 2. Mirrors the mobile Country screen (iOS OnBoardingCountryViewController /
       Android OnboardingCountryFragment), including the "Hey <name>," greeting.
       The list is fetched once and filtered in the browser — it's ~350 rows, so a
       round-trip per keystroke would be slower and no more accurate. -->
  <div>
    <OnBoardingStepIndicator :current="4" />

    <div class="back-link mb-3" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>{{ $t('on_boarding.profile_steps.back') }}</span>
    </div>

    <h2 class="auth-heading">{{ heading }}</h2>
    <p class="auth-sub">{{ $t('on_boarding.profile_country_page.subtitle') }}</p>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <div v-if="!countriesFailed" class="pro-field mb-2">
      <label class="pro-field__label" for="countrySearch">
        {{ $t('on_boarding.profile_country_page.placeholder') }}
      </label>
      <input
        id="countrySearch"
        ref="searchInput"
        v-model="query"
        type="text"
        class="form-control form-control-lg pro-field__input"
        :placeholder="$t('on_boarding.profile_country_page.search_placeholder')"
        autocomplete="country-name"
        role="combobox"
        aria-controls="countryResults"
        :aria-expanded="visibleCountries.length > 0"
      >
    </div>

    <p v-if="isLoadingCountries" class="country-status">
      {{ $t('on_boarding.profile_country_page.loading') }}
    </p>
    <!-- A failed fetch is called out separately from "no match": an empty list
         looks identical to a bad search, and without a way forward the user is
         stuck on a step whose Continue can never enable. -->
    <div v-else-if="countriesFailed" class="country-status">
      <p class="mb-2">{{ $t('on_boarding.profile_country_page.load_failed') }}</p>
      <button type="button" class="btn pro-btn pro-btn--secondary" @click="retry">
        <span class="material-icons">refresh</span>
        <span>{{ $t('on_boarding.profile_country_page.retry') }}</span>
      </button>
    </div>
    <p v-else-if="visibleCountries.length === 0" class="country-status">
      {{ $t('on_boarding.profile_country_page.no_results') }}
    </p>

    <ul v-else id="countryResults" class="country-list" role="listbox">
      <li v-for="country in visibleCountries" :key="country.id">
        <button
          type="button"
          class="country-option"
          :class="{ 'country-option--selected': String(country.id) === String(selectedCountryId) }"
          role="option"
          :aria-selected="String(country.id) === String(selectedCountryId)"
          @click="select(country)"
        >
          <span>{{ country.name }}</span>
          <span v-if="String(country.id) === String(selectedCountryId)" class="material-icons">check</span>
        </button>
      </li>
    </ul>

    <button
      type="button"
      class="btn pro-btn pro-btn--accent w-100 mt-3"
      :disabled="!canSubmit"
      @click="submit"
    >
      <span class="material-icons">arrow_forward</span>
      <span>{{ continueLabel }}</span>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';
import { filterCountries, type SignupCountry } from '../../../utils/helpers/signupProfile';

// Enough to scroll through without rendering all ~350 rows on every keystroke.
const MAX_VISIBLE = 60;

export default defineComponent({
  name: 'OnBoardingProfileCountryPage',
  components: { OnBoardingStepIndicator },
  data() {
    return {
      query: '',
      selectedCountryId: this.$store.state.onBoarding.profileCountryId as string | number | null,
    };
  },
  computed: {
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    isLoadingCountries(): boolean {
      return this.$store.state.onBoarding.countriesLoading;
    },
    countries(): SignupCountry[] {
      return this.$store.state.onBoarding.countries;
    },
    countriesFailed(): boolean {
      return this.$store.state.onBoarding.countriesFailed;
    },
    continueLabel(): string {
      return this.countriesFailed
        ? this.$t('on_boarding.profile_country_page.continue_without')
        : this.$t('on_boarding.profile_country_page.continue');
    },
    firstName(): string {
      return this.$store.state.onBoarding.profileFirstName;
    },
    // Falls back to the name-less wording rather than rendering "Hey ,".
    heading(): string {
      return this.firstName
        ? this.$t('on_boarding.profile_country_page.title', { name: this.firstName })
        : this.$t('on_boarding.profile_country_page.title_no_name');
    },
    visibleCountries(): SignupCountry[] {
      return filterCountries(this.countries, this.query).slice(0, MAX_VISIBLE);
    },
    canSubmit(): boolean {
      // When the list couldn't load there is nothing to pick, so the step must
      // still be passable — the country is simply left unset.
      return this.selectedCountryId != null || this.countriesFailed;
    },
  },
  mounted() {
    // The list may still be in flight when the step opens; the action is a
    // no-op once it has loaded.
    this.$store.dispatch('onBoarding/fetchCountries');
    (this.$refs.searchInput as HTMLInputElement | undefined)?.focus();
  },
  methods: {
    select(country: SignupCountry) {
      this.selectedCountryId = country.id;
      this.$store.commit('onBoarding/setError', null);
    },
    retry() {
      this.$store.dispatch('onBoarding/retryFetchCountries');
    },
    submit() {
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.$t('on_boarding.profile_country_page.error_missing'));
        return;
      }
      this.$store.dispatch('onBoarding/submitProfileCountry', this.selectedCountryId);
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.35rem;
}
.auth-sub {
  color: var(--pro-text-muted);
  font-size: 0.95rem;
  margin: 0 0 1.25rem;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--pro-text-muted);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover {
  color: #fff;
}

.country-status {
  color: var(--pro-text-muted);
  font-size: 0.9rem;
  margin: 0.75rem 0;
}

/* Capped height so the step never pushes the Continue button off a short
   viewport — the list scrolls instead of the page. */
.country-list {
  list-style: none;
  margin: 0;
  padding: 0;
  /* Capped so this step stands roughly level with the others — an outsized
     list made the card visibly jump when moving between steps. Viewport-
     relative as well, so it still fits a short laptop screen. The list scrolls
     internally rather than growing the card. */
  max-height: min(175px, 22vh);
  overflow-y: auto;
  border: 1px solid var(--pro-border-strong);
  border-radius: 12px;
}
.country-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.65rem 0.9rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  text-align: left;
  cursor: pointer;
}
.country-option:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
.country-option--selected {
  background: rgba(253, 223, 0, 0.12);
  color: #fff;
  font-weight: 700;
}
.country-option .material-icons {
  font-size: 18px;
  color: var(--yellow, #fddf00);
}

/* Very short screens: give the list less room rather than letting the step
   grow past the fold. It still scrolls internally. */
@media (max-height: 760px) {
  .auth-sub { margin-bottom: 0.6rem; }
  .country-list { max-height: 18vh; }
}
</style>
